/**
 * macOS line-editing keys for the terminal (Terminal board card 1, 21.10.2026).
 *
 * xterm.js sends Option+←/→ as `ESC[1;3D` / `ESC[1;3C`. zsh and bash bind
 * nothing to those by default, so they echo as stray `;3D` / `;3C` — the
 * "random characters" a Mac user sees while editing a command. Like VS Code's
 * terminal and iTerm2's "Natural text editing", we send what the shells DO
 * understand instead:
 *
 *   ⌥←  ESC b   (backward-word)        ⌘←  Ctrl+A  (start of line)
 *   ⌥→  ESC f   (forward-word)         ⌘→  Ctrl+E  (end of line)
 *   ⌥⌫  ESC DEL (backward-kill-word)   ⌘⌫  Ctrl+U  (kill to line start)
 *
 * Only on macOS, only on keydown, and only with exactly that one modifier, so
 * pane shortcuts with more modifiers still reach the key filter.
 */
const MAC_KEYS = {
  alt: { ArrowLeft: '\x1bb', ArrowRight: '\x1bf', Backspace: '\x1b\x7f' },
  meta: { ArrowLeft: '\x01', ArrowRight: '\x05', Backspace: '\x15' },
};

/** The bytes to send for `e` on a Mac, or null to let xterm handle the key. Pure. */
export function macEditSequence(e, isMac) {
  if (!isMac || !e || e.type !== 'keydown' || e.shiftKey || e.ctrlKey) return null;
  if (e.altKey && !e.metaKey) return MAC_KEYS.alt[e.key] ?? null;
  if (e.metaKey && !e.altKey) return MAC_KEYS.meta[e.key] ?? null;
  return null;
}

/**
 * Wrap a terminal's custom key handler: a Mac editing key is sent via `send`
 * and kept from xterm; everything else goes to `next` (the pane's key filter).
 * xterm's custom handler sees keydown, keypress and keyup — the matching
 * keyup is let through untouched, it produces no input.
 */
export function withMacEditKeys(next, send, isMac) {
  return (e) => {
    const seq = macEditSequence(e, isMac);
    if (seq != null) {
      e.preventDefault?.();
      send(seq);
      return false;
    }
    return next ? next(e) : true;
  };
}
